// Bots: Bot Management (CRUD, publish/pause), Bot Configuration (`config`) and Appearance &
// Branding (`appearance`) all read and write the same bot record by id.
import { botsStore, botView, botLimitFor, planFor } from "../core";
import { makeBot } from "../fixtures";
import { simulateBotReply } from "../botEngine";
import { AI_MODELS, modelAvailable, defaultModelFor, consumeAiReply } from "../ai";
import { ok, fail, validationFail, genId, nowIso } from "../helpers";
import { collect, hexError, intRangeError, lengthError, oneOfError } from "../validate";

const TOP_LEVEL = ["name", "description", "model", "greeting"];
const modelProblem = (model, plan) => {
  const m = AI_MODELS.find((x) => x.id === model);
  if (!m) return { code: 422, message: "Choose a supported model." };
  return modelAvailable(model, plan) ? null : { code: 403, message: `${m.label} is available on the ${m.minPlan} plan. Your workspace is on ${plan}.` };
};

const TONES = ["friendly", "professional", "casual", "empathetic", "formal"];
const LANGS = ["en", "hi", "te", "ta", "es", "fr", "de", "ar", "pt"];
const FONTS = ["Poppins", "Inter", "Roboto", "Open Sans", "Nunito", "Montserrat"];
const DATA_IMAGE = /^data:image\/(png|jpe?g|gif|webp|svg\+xml);base64,[A-Za-z0-9+/=]+$/;
const MAX_IMAGE_CHARS = 1500000; // ~1 MB once base64-decoded

const imageError = (v, label) => {
  if (!v) return "";
  if (!DATA_IMAGE.test(v)) return `${label} must be a PNG, JPG, GIF, WEBP or SVG image.`;
  return v.length > MAX_IMAGE_CHARS ? `${label} is too large — use an image under 1 MB.` : "";
};
const listError = (v, { max, itemMax, label }) => {
  if (v === undefined) return "";
  if (!Array.isArray(v)) return `${label} must be a list.`;
  if (v.length > max) return `${label} can have at most ${max} items.`;
  const bad = v.find((x) => typeof x !== "string" || !x.trim() || x.length > itemMax);
  return bad !== undefined ? `Each item in ${label} must be 1–${itemMax} characters.` : "";
};

/** Bot Configuration limits mirror the panels' own client rules. */
function configFields(c) {
  const h = c.handoff || {};
  const has = (k) => c[k] !== undefined;
  return collect([
    has("tone") && ["tone", oneOfError(c.tone, TONES, "Tone")],
    has("primaryLanguage") && ["primaryLanguage", oneOfError(c.primaryLanguage, LANGS, "Language")],
    has("supportedLanguages") && ["supportedLanguages", !Array.isArray(c.supportedLanguages) || !c.supportedLanguages.length || c.supportedLanguages.some((l) => !LANGS.includes(l)) ? "Choose at least one supported language." : ""],
    has("instructions") && ["instructions", lengthError(c.instructions, { max: 2000, label: "Instructions" })],
    has("fallbackMessage") && ["fallbackMessage", lengthError(c.fallbackMessage, { min: 10, max: 300, label: "Fallback message" })],
    has("prohibitedTopics") && ["prohibitedTopics", listError(c.prohibitedTopics, { max: 30, itemMax: 80, label: "Prohibited topics" })],
    has("customProhibitedTopics") && ["customProhibitedTopics", listError(c.customProhibitedTopics, { max: 20, itemMax: 80, label: "Custom topics" })],
    has("responseBoundaryScope") && ["responseBoundaryScope", lengthError(c.responseBoundaryScope, { max: 500, label: "Allowed-topic description" })],
    has("responseBoundaryMessage") && ["responseBoundaryMessage", lengthError(c.responseBoundaryMessage, { max: 300, label: "Boundary response" })],
    has("responseLength") && ["responseLength", oneOfError(c.responseLength, ["short", "balanced", "detailed"], "Response length")],
    c.leadCapture !== undefined && ["leadCapture.phone", oneOfError((c.leadCapture || {}).phone, ["off", "optional", "required"], "Phone field")],
    c.leadCapture !== undefined && ["leadCapture.company", typeof (c.leadCapture || {}).company === "boolean" ? "" : "Company field must be on or off."],
    c.leadCapture !== undefined && ["leadCapture.message", typeof (c.leadCapture || {}).message === "boolean" ? "" : "Message field must be on or off."],
    c.handoff !== undefined && ["handoff.message", h.message ? lengthError(h.message, { min: 10, max: 300, label: "Handoff message" }) : ""],
    c.handoff !== undefined && ["handoff.offlineMessage", h.offlineMessage ? lengthError(h.offlineMessage, { min: 10, max: 300, label: "Offline message" }) : ""],
    c.handoff !== undefined && ["handoff.keywords", listError(h.keywords, { max: 20, itemMax: 40, label: "Handoff keywords" })]
  ].filter(Boolean));
}

/** Appearance & Branding limits mirror the inputs' min/max on the Appearance page. */
function appearanceFields(a) {
  const has = (k) => a[k] !== undefined;
  return collect([
    has("botName") && ["botName", lengthError(a.botName, { min: 1, max: 32, label: "Bot display name" })],
    has("description") && ["description", lengthError(a.description, { max: 120, label: "Short description" })],
    has("headerText") && ["headerText", lengthError(a.headerText, { max: 50, label: "Header text" })],
    has("headerStatus") && ["headerStatus", oneOfError(a.headerStatus, ["Online", "Available", "Offline"], "Header status")],
    has("welcome") && ["welcome", lengthError(a.welcome, { min: 1, max: 240, label: "Welcome message" })],
    has("primary") && ["primary", hexError(a.primary, "Primary color")],
    has("secondary") && ["secondary", hexError(a.secondary, "Secondary color")],
    has("font") && ["font", oneOfError(a.font, FONTS, "Font")],
    has("launcher") && ["launcher", oneOfError(a.launcher, ["Round Blue", "Soft Square", "Minimal", "Gradient"], "Launcher style")],
    has("position") && ["position", oneOfError(a.position, ["Bottom right", "Bottom left"], "Position")],
    has("widgetPosition") && ["widgetPosition", oneOfError(a.widgetPosition, ["bottom-right", "bottom-left", "top-right", "top-left"], "Widget position")],
    has("launcherSize") && ["launcherSize", intRangeError(a.launcherSize, { min: 44, max: 80, label: "Launcher size" })],
    has("widgetWidth") && ["widgetWidth", intRangeError(a.widgetWidth, { min: 280, max: 520, label: "Desktop width" })],
    has("widgetHeight") && ["widgetHeight", intRangeError(a.widgetHeight, { min: 420, max: 760, label: "Desktop height" })],
    has("mobileWidth") && ["mobileWidth", intRangeError(a.mobileWidth, { min: 280, max: 520, label: "Mobile width" })],
    has("mobileHeight") && ["mobileHeight", intRangeError(a.mobileHeight, { min: 420, max: 760, label: "Mobile height" })],
    has("desktopOffsetX") && ["desktopOffsetX", intRangeError(a.desktopOffsetX, { min: 0, max: 120, label: "Horizontal offset" })],
    has("desktopOffsetY") && ["desktopOffsetY", intRangeError(a.desktopOffsetY, { min: 0, max: 120, label: "Vertical offset" })],
    has("avatar") && ["avatar", imageError(a.avatar, "Avatar")],
    has("logo") && ["logo", imageError(a.logo, "Logo")]
  ].filter(Boolean));
}

const storageFull = () =>
  fail(507, "STORAGE_FULL", "Browser storage is full — try a smaller image or remove unused data.");

function findBot(tenantId, id) {
  const bots = botsStore.all();
  return { bots, bot: bots.find((b) => b.id === id && b.tenantId === tenantId) };
}

export const botRoutes = [
  {
    method: "GET", pattern: /^\/api\/bots$/, permission: "bots:manage",
    handler: ({ tenantId, scenario }) => {
      const items = scenario === "empty" ? [] : botsStore.all().filter((b) => b.tenantId === tenantId).map(botView);
      return ok(200, items, { total: items.length, tenantId, plan: planFor(tenantId), botLimit: botLimitFor(tenantId) });
    }
  },
  {
    method: "POST", pattern: /^\/api\/bots$/, permission: "bots:manage",
    handler: ({ tenantId, body }) => {
      const fields = {};
      Object.assign(fields, collect([
        ["name", lengthError(body.name, { min: 3, max: 50, label: "Bot name", required: true })],
        ["description", lengthError(body.description, { max: 200, label: "Description" })],
        ["greeting", lengthError(body.greeting, { max: 300, label: "Greeting" })]
      ]) || {});
      const bots = botsStore.all();
      const limit = botLimitFor(tenantId);
      if (bots.filter((b) => b.tenantId === tenantId).length >= limit) {
        return fail(403, "PLAN_LIMIT_REACHED", `Your ${planFor(tenantId)} plan allows ${limit} bot${limit === 1 ? "" : "s"}. Upgrade your plan to add more.`, { limit, plan: planFor(tenantId) });
      }
      if (body.name && bots.some((b) => b.tenantId === tenantId && b.name.toLowerCase() === body.name.trim().toLowerCase())) {
        fields.name = "A bot with this name already exists.";
      }
      const plan = planFor(tenantId);
      const modelIssue = body.model ? modelProblem(body.model, plan) : null;
      if (modelIssue && modelIssue.code === 422) fields.model = modelIssue.message;
      if (Object.keys(fields).length) return validationFail(fields, Object.values(fields)[0]);
      if (modelIssue) return fail(403, "MODEL_NOT_AVAILABLE", modelIssue.message, { plan, model: body.model });
      const stamp = nowIso();
      const bot = makeBot({
        id: genId("bot"), tenantId, name: body.name.trim(), description: (body.description || "").trim(),
        model: body.model || defaultModelFor(plan), greeting: body.greeting || "Hi! How can I help you today?",
        ownerRole: body.ownerRole || "business_admin", status: "inactive", version: 1, createdAt: stamp, updatedAt: stamp
      });
      if (!botsStore.save([bot, ...bots])) return storageFull();
      return ok(201, botView(bot));
    }
  },
  {
    method: "GET", pattern: /^\/api\/bots\/([\w-]+)$/, permission: "bots:manage",
    handler: ({ tenantId, match }) => {
      const { bot } = findBot(tenantId, match[1]);
      if (!bot) return fail(404, "NOT_FOUND", "Bot not found.");
      return ok(200, botView(bot));
    }
  },
  {
    method: "PATCH", pattern: /^\/api\/bots\/([\w-]+)$/, permission: "bots:manage",
    handler: ({ tenantId, match, body }) => {
      const { bots, bot } = findBot(tenantId, match[1]);
      if (!bot) return fail(404, "NOT_FOUND", "Bot not found.");
      const fields = {};
      Object.assign(fields, collect([
        body.name !== undefined && ["name", lengthError(body.name, { min: 3, max: 50, label: "Bot name", required: true })],
        body.description !== undefined && ["description", lengthError(body.description, { max: 200, label: "Description" })],
        body.greeting !== undefined && ["greeting", lengthError(body.greeting, { min: 1, max: 300, label: "Greeting" })]
      ].filter(Boolean)) || {});
      if (body.config !== undefined) {
        if (typeof body.config !== "object" || body.config === null) fields.config = "Configuration must be an object.";
        else Object.assign(fields, configFields(body.config) || {});
      }
      if (body.appearance !== undefined) {
        if (typeof body.appearance !== "object" || body.appearance === null) fields.appearance = "Appearance must be an object.";
        else Object.assign(fields, appearanceFields(body.appearance) || {});
      }
      if (body.status !== undefined && !["active", "inactive"].includes(body.status)) fields.status = "Status must be active or inactive.";
      const modelIssue = body.model !== undefined && body.model !== bot.model ? modelProblem(body.model, planFor(tenantId)) : null;
      if (modelIssue && modelIssue.code === 422) fields.model = modelIssue.message;
      if (Object.keys(fields).length) return validationFail(fields, Object.values(fields)[0]);
      if (modelIssue) return fail(403, "MODEL_NOT_AVAILABLE", modelIssue.message, { plan: planFor(tenantId), model: body.model });

      let versionBump = false;
      TOP_LEVEL.forEach((key) => {
        if (body[key] !== undefined && body[key] !== bot[key]) { bot[key] = typeof body[key] === "string" ? body[key].trim() : body[key]; versionBump = true; }
      });
      if (body.status !== undefined) bot.status = body.status;
      if (body.config && typeof body.config === "object") {
        const { handoff, ...rest } = body.config;
        bot.config = { ...bot.config, ...rest, ...(handoff ? { handoff: { ...(bot.config.handoff || {}), ...handoff, triggers: { ...((bot.config.handoff || {}).triggers || {}), ...(handoff.triggers || {}) } } } : {}) };
      }
      if (body.appearance && typeof body.appearance === "object") bot.appearance = { ...bot.appearance, ...body.appearance };
      if (versionBump) bot.version += 1;
      bot.updatedAt = nowIso();
      if (!botsStore.save(bots)) return storageFull();
      return ok(200, botView(bot));
    }
  },
  {
    method: "DELETE", pattern: /^\/api\/bots\/([\w-]+)$/, permission: "bots:manage",
    handler: ({ tenantId, match }) => {
      const { bots, bot } = findBot(tenantId, match[1]);
      if (!bot) return fail(404, "NOT_FOUND", "Bot not found.");
      botsStore.save(bots.filter((b) => b.id !== bot.id));
      return ok(200, { id: bot.id });
    }
  },
  {
    method: "POST", pattern: /^\/api\/bots\/([\w-]+)\/publish$/, permission: "bots:manage",
    handler: ({ tenantId, match }) => {
      const { bots, bot } = findBot(tenantId, match[1]);
      if (!bot) return fail(404, "NOT_FOUND", "Bot not found.");
      bot.status = "active";
      bot.updatedAt = nowIso();
      botsStore.save(bots);
      return ok(200, botView(bot));
    }
  },
  {
    method: "POST", pattern: /^\/api\/bots\/([\w-]+)\/test-message$/, permission: "bots:manage",
    handler: ({ tenantId, match, body }) => {
      const { bot } = findBot(tenantId, match[1]);
      if (!bot) return fail(404, "NOT_FOUND", "Bot not found.");
      if (!body.message || !body.message.trim()) return validationFail({ message: "Enter a test message." });
      if (!consumeAiReply(tenantId, bot.id).allowed) return fail(429, "AI_QUOTA_EXCEEDED", "Your workspace has used all its AI replies for this month.");
      return ok(200, { ...simulateBotReply(bot, null, body.message), botId: bot.id, respondedAt: nowIso() });
    }
  }
];
